import React, { useState, useEffect, useCallback, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import { useIntl, FormattedMessage } from 'react-intl'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        position: 'relative',
        boxSizing: 'border-box',
    },
    scrollableContent: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        padding: '20px 20px 0',
        width: '100%',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            gap: 18,
            padding: '16px 12px 0',
        },
    },
    starsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    starIcon: {
        fontSize: 56,
        transition: 'all 400ms ease',
        '@media (max-width: 599px)': { fontSize: 40 },
    },
    starFilled: {
        color: CapeeshColors.YellowBright,
        transform: 'scale(1)',
    },
    starEmpty: {
        color: 'rgba(255, 255, 255, 0.25)',
        transform: 'scale(0.85)',
    },
    starAnimating: {
        animation: '$starPop 400ms ease-out',
    },
    '@keyframes starPop': {
        '0%': { transform: 'scale(0.3)', opacity: 0 },
        '60%': { transform: 'scale(1.2)', opacity: 1 },
        '100%': { transform: 'scale(1)', opacity: 1 },
    },
    scoreSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    scoreText: {
        fontFamily: 'Rubik',
        fontSize: 32,
        fontWeight: 700,
        color: '#FFFFFF',
        '@media (max-width: 599px)': { fontSize: 26 },
    },
    scoreLabel: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    xpBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 20px',
        borderRadius: 24,
        backgroundColor: 'rgba(255, 210, 86, 0.2)',
        border: '2px solid rgba(255, 210, 86, 0.4)',
    },
    xpText: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 600,
        color: CapeeshColors.YellowBright,
    },
    feedbackCard: {
        width: '100%',
        maxWidth: 400,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 20,
        padding: '24px 24px',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            padding: '16px 16px',
            borderRadius: 14,
            gap: 10,
        },
    },
    feedbackTitle: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
        '@media (max-width: 599px)': { fontSize: 18 },
    },
    feedbackBody: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(255, 255, 255, 0.85)',
        textAlign: 'center',
    },
    statsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        justifyContent: 'center',
    },
    statItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
    },
    statValue: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 700,
        color: '#FFFFFF',
    },
    statLabel: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        maxWidth: 320,
        flexShrink: 0,
        padding: '16px 20px 20px',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            padding: '12px 12px 16px',
        },
        '@media (max-height: 679px)': {
            flexDirection: 'row',
            gap: 10,
            maxWidth: '100%',
        },
        '@media (max-height: 579px)': {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(26, 26, 46, 0.95) 30%)',
            padding: '32px 20px 16px',
        },
    },
    finishButton: {
        width: '100%',
        padding: '16px 0',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 17,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
        '@media (max-height: 679px)': {
            flex: 1,
            padding: '12px 0',
            fontSize: 15,
        },
    },
    retryButton: {
        width: '100%',
        padding: '14px 0',
        borderRadius: 28,
        border: '2px solid rgba(255, 255, 255, 0.4)',
        backgroundColor: 'transparent',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
        '@media (max-height: 679px)': {
            flex: 1,
            padding: '10px 0',
            fontSize: 14,
        },
    },
}))

/**
 * DebriefNode — shows lesson results: stars, score, XP, feedback.
 * Provides Finish and Try Again buttons.
 */
const DebriefNode = ({
    node,
    imageMap,
    audioMap,
    onNodeComplete,
    scoring,
    lessonDto,
    onRestart,
    onQuit,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const [animatedStars, setAnimatedStars] = useState(0)

    const hasFailed = scoring?.hasFailed || false
    const stars = hasFailed ? 0 : (scoring?.stars || 0)
    const normalizedScore = scoring?.normalizedScore || 0
    const lessonXp = scoring?.lessonXp || 0
    const answersSubmitted = scoring?.answersSubmitted || 0
    const correctAnswersSubmitted = scoring?.correctAnswersSubmitted || 0

    // Pass/fail feedback from node options (threshold matches Unity DefaultOneStarThreshold)
    const passText = node.options?.[0]?.text || intl.formatMessage(playerMessages.greatJob)
    const failText = node.options?.[1]?.text || intl.formatMessage(playerMessages.keepPracticing)
    const passed = !hasFailed && normalizedScore >= 0.2
    const feedbackText = passed ? passText : failText

    // Star animation
    useEffect(() => {
        let timer
        const animateStars = (count) => {
            if (count > stars) return
            timer = setTimeout(() => {
                setAnimatedStars(count)
                animateStars(count + 1)
            }, 400)
        }
        animateStars(1)
        return () => clearTimeout(timer)
    }, [stars])

    const handleFinish = useCallback(() => {
        if (onQuit) {
            onQuit()
        }
    }, [onQuit])

    const handleRetry = useCallback(() => {
        if (onRestart) {
            onRestart()
        }
    }, [onRestart])

    return (
        <div className={classes.container}>
            <div className={classes.scrollableContent}>
                {/* Stars */}
                <div className={classes.starsContainer}>
                    {[1, 2, 3].map((n) => {
                        const isFilled = n <= animatedStars
                        const isAnimating = n === animatedStars && n <= stars
                        return isFilled ? (
                            <StarIcon
                                key={n}
                                className={`${classes.starIcon} ${classes.starFilled} ${
                                    isAnimating ? classes.starAnimating : ''
                                }`}
                            />
                        ) : (
                            <StarBorderIcon
                                key={n}
                                className={`${classes.starIcon} ${classes.starEmpty}`}
                            />
                        )
                    })}
                </div>

                {/* Score */}
                <div className={classes.scoreSection}>
                    <Typography className={classes.scoreText}>
                        {Math.round(normalizedScore * 100)}%
                    </Typography>
                    <Typography className={classes.scoreLabel}><FormattedMessage {...playerMessages.score} /></Typography>
                </div>

                {/* XP */}
                <div className={classes.xpBadge}>
                    <Typography className={classes.xpText}><FormattedMessage {...playerMessages.xpBadge} values={{ xp: lessonXp }} /></Typography>
                </div>

                {/* Feedback */}
                <div className={classes.feedbackCard}>
                    <Typography className={classes.feedbackTitle}>
                        {passed ? <FormattedMessage {...playerMessages.lessonComplete} /> : <FormattedMessage {...playerMessages.tryAgain} />}
                    </Typography>
                    <Typography className={classes.feedbackBody}>{renderFormattedText(feedbackText)}</Typography>

                    {/* Stats */}
                    <div className={classes.statsRow}>
                        <div className={classes.statItem}>
                            <Typography className={classes.statValue}>
                                {correctAnswersSubmitted}
                            </Typography>
                            <Typography className={classes.statLabel}><FormattedMessage {...playerMessages.correct} /></Typography>
                        </div>
                        <div className={classes.statItem}>
                            <Typography className={classes.statValue}>
                                {answersSubmitted}
                            </Typography>
                            <Typography className={classes.statLabel}><FormattedMessage {...playerMessages.answered} /></Typography>
                        </div>
                        <div className={classes.statItem}>
                            <Typography className={classes.statValue}>{stars}</Typography>
                            <Typography className={classes.statLabel}><FormattedMessage {...playerMessages.stars} /></Typography>
                        </div>
                    </div>
                </div>
            </div>

            {/* Buttons */}
            <div className={classes.buttonsContainer}>
                <button className={classes.finishButton} onClick={handleFinish}>
                    <FormattedMessage {...playerMessages.finish} />
                </button>
                <button className={classes.retryButton} onClick={handleRetry}>
                    <FormattedMessage {...playerMessages.tryAgain} />
                </button>
            </div>
        </div>
    )
}

export default DebriefNode
